import type { Maybe } from '@adornis/base/utilTypes.js';
import { Entity, Field } from '@adornis/baseql/decorators.js';
import { DataLoadedField } from '@adornis/baseql/decorators/data-loaded-field.js';
import { MongoEntity } from '@adornis/baseql/entities/mongoEntity.js';
import { Timeframe } from '@adornis/bh-timeframes/entities/Timeframe.js';
import { Price } from '@adornis/payments/db/prices.js';
import { validate } from '@adornis/validation/decorators.js';
import { ValidationError } from '@adornis/validation/errors/ValidationError.js';
import { nonOptional } from '@adornis/validation/functions/nonOptional.js';
import type { Document } from 'bson';
import type { InsertOneResult } from 'mongodb';
import { Product } from './products.js';

@Entity()
export class Bundle extends MongoEntity {
  static override _class = 'Bundle';
  static override _collectionName = 'bundles';

  @validate(nonOptional())
  @Field(type => String)
  name!: string;

  @validate(nonOptional())
  @Field(type => String)
  institutionID!: string;

  @Field(type => Boolean, { default: v => (v === null ? false : !!v) })
  hasPrice?: boolean;

  @validate(async options => {
    await nonOptional()(options);
    const price = options.value as Price;
    const value = price.unitAmount;
    if (value > 100000 * 100)
      throw new ValidationError('Preis darf 100.000 Eur nicht überschreiten', {
        translationKey: 'max_price_err',
        key: options.key,
      });
  })
  @Field(type => Price, { default: v => v ?? new Price({ unitAmount: 1 }) })
  price!: Price;

  get getUnitAmount() {
    return this.hasPrice ? this.price.unitAmount : null;
  }
  get getCalcAmount() {
    return this.hasPrice ? this.price.unitAmount : 0;
  }

  @Field(type => String)
  timeframeID!: string;

  @DataLoadedField(type => Timeframe, ({ timeframeID }) => timeframeID)
  timeframe: Maybe<Timeframe>;

  @validate(nonOptional())
  @Field(type => [String], { default: v => v ?? [] })
  productIDs!: string[];

  @DataLoadedField(type => [Product], ({ productIDs }) => productIDs)
  products: Maybe<Product[]>;

  @validate(nonOptional({ allowFalsy: true }))
  @Field(type => Boolean, { default: v => v ?? false })
  isActive!: boolean;

  @Field(type => Date)
  publishDate?: Date;

  static override async beforeUpdate(
    oldEntity: { _id?: string | undefined; _class?: string | undefined },
    newEntity: Bundle,
  ) {
    await super.beforeUpdate(oldEntity, newEntity);
    const { updateCheckProductPrices } = await import('../api/bundle.js');
    await updateCheckProductPrices(newEntity);
  }

  static override async afterCreate(entity: Bundle, result: InsertOneResult<Document>): Promise<void> {
    await super.afterCreate(entity, result);
    const { updateCheckProductPrices } = await import('../api/bundle.js');
    await updateCheckProductPrices(entity);
  }
}
